/**
 * HashMedora Game API
 * Client-side placeholder implementation using localStorage.
 * UI components call these functions directly.
 * When backend is ready, only this file needs updating.
 */

import { GAMES } from '../data/games';
import { storage } from './storage';

// Generate a pseudo-random draw result for a game
function generateDrawResult(gameId, drawId) {
  const game = GAMES[gameId];
  if (!game) return null;

  const range = game.numberRange.max - game.numberRange.min + 1;
  const winningNumber = Math.floor(Math.random() * range) + game.numberRange.min;

  // Generate neighbors
  const neighbors = [];
  const halfNeighbors = Math.floor(game.neighborCount / 2);
  for (let i = 1; i <= halfNeighbors; i++) {
    const lower = winningNumber - i;
    const upper = winningNumber + i;
    if (lower >= game.numberRange.min) neighbors.push(lower);
    if (upper <= game.numberRange.max) neighbors.push(upper);
  }

  // Ensure we have exactly neighborCount neighbors
  while (neighbors.length < game.neighborCount) {
    const randomNeighbor = Math.floor(Math.random() * range) + game.numberRange.min;
    if (randomNeighbor !== winningNumber && !neighbors.includes(randomNeighbor)) {
      neighbors.push(randomNeighbor);
    }
  }

  return {
    drawId,
    gameId,
    winningNumber,
    neighbors: neighbors.slice(0, game.neighborCount),
    timestamp: Date.now(),
    totalPool: game.grandPrizePool || game.maxPrize * 10,
    totalWinners: 1 + game.neighborCount,
  };
}

// Get game configuration
export function getGameConfig(gameId) {
  return GAMES[gameId] || null;
}

// Get all game configurations
export function getAllGameConfigs() {
  return Object.values(GAMES);
}

// Place a bet
export async function placeBet(walletAddress, gameId, numbers, autoDraw = false) {
  if (!walletAddress) throw new Error('Wallet not connected');
  if (!GAMES[gameId]) throw new Error('Invalid game');
  if (!numbers || numbers.length === 0) throw new Error('No numbers selected');

  const game = GAMES[gameId];
  const totalCost = numbers.length * game.entryFee;

  // Generate a unique bet ID
  const betId = `bet_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const bet = {
    id: betId,
    walletAddress: walletAddress.toLowerCase(),
    gameId,
    numbers: [...numbers],
    cost: totalCost,
    autoDraw,
    status: 'pending',
    timestamp: Date.now(),
    drawId: null,
    result: null,
    prize: 0,
  };

  // Save bet to storage
  const bets = storage.getList('bets');
  bets.push(bet);
  storage.set('bets', bets);

  // Update player stats
  updatePlayerStats(walletAddress, 'bet', totalCost);

  return {
    success: true,
    betId,
    totalCost,
    message: `Successfully placed bet on ${game.shortName} for ${numbers.length} number(s)`,
  };
}

// Get current draw info for a game
export async function getCurrentDraw(gameId) {
  const draws = storage.get('draws', {});
  const gameDraws = draws[gameId] || [];

  // If no draws exist, create the first one
  if (gameDraws.length === 0) {
    const newDraw = generateDrawResult(gameId, `draw_${gameId}_1`);
    draws[gameId] = [newDraw];
    storage.set('draws', draws);
    return {
      ...newDraw,
      status: 'open',
      timeRemaining: 3600000, // 1 hour
    };
  }

  const latestDraw = gameDraws[gameDraws.length - 1];
  const timeSinceDraw = Date.now() - latestDraw.timestamp;
  const drawInterval = 3600000; // 1 hour between draws

  if (timeSinceDraw >= drawInterval) {
    // Create new draw
    const newDraw = generateDrawResult(gameId, `draw_${gameId}_${gameDraws.length + 1}`);
    draws[gameId].push(newDraw);
    storage.set('draws', draws);

    // Process pending bets for previous draw
    await processDrawResults(gameId, latestDraw);

    return {
      ...newDraw,
      status: 'open',
      timeRemaining: drawInterval,
    };
  }

  return {
    ...latestDraw,
    status: 'open',
    timeRemaining: drawInterval - timeSinceDraw,
  };
}

// Get draw results for a specific draw
export async function getDrawResults(gameId, drawId) {
  const draws = storage.get('draws', {});
  const gameDraws = draws[gameId] || [];
  return gameDraws.find((d) => d.drawId === drawId) || null;
}

// Get recent draws for a game
export async function getRecentDraws(gameId, limit = 10) {
  const draws = storage.get('draws', {});
  const gameDraws = draws[gameId] || [];
  return gameDraws.slice(-limit).reverse();
}

// Process draw results and update bets
async function processDrawResults(gameId, drawResult) {
  const bets = storage.getList('bets');
  const game = GAMES[gameId];

  const updatedBets = bets.map((bet) => {
    if (bet.gameId !== gameId || bet.status !== 'pending' || !bet.drawId) {
      return bet;
    }

    if (bet.drawId !== drawResult.drawId) return bet;

    const winningNumber = drawResult.winningNumber;
    let prize = 0;
    let result = 'lost';

    if (bet.numbers.includes(winningNumber)) {
      prize = game.directPrize;
      result = 'won_direct';
    } else {
      const neighborMatch = bet.numbers.find((n) => drawResult.neighbors.includes(n));
      if (neighborMatch !== undefined) {
        prize = game.neighborPrize;
        result = 'won_neighbor';
      }
    }

    return {
      ...bet,
      status: 'completed',
      result,
      prize,
      completedAt: Date.now(),
    };
  });

  storage.set('bets', updatedBets);
}

// Update player stats
function updatePlayerStats(walletAddress, action, amount) {
  const normalizedAddress = walletAddress.toLowerCase();
  const stats = storage.get(`player_stats_${normalizedAddress}`, {
    totalBets: 0,
    totalSpent: 0,
    totalWon: 0,
    totalWins: 0,
    totalLosses: 0,
    currentStreak: 0,
    bestStreak: 0,
  });

  if (action === 'bet') {
    stats.totalBets += 1;
    stats.totalSpent += amount;
  } else if (action === 'win') {
    stats.totalWon += amount;
    stats.totalWins += 1;
    stats.currentStreak += 1;
    if (stats.currentStreak > stats.bestStreak) {
      stats.bestStreak = stats.currentStreak;
    }
  } else if (action === 'loss') {
    stats.totalLosses += 1;
    stats.currentStreak = 0;
  }

  storage.set(`player_stats_${normalizedAddress}`, stats);
}

// Quick pick random numbers for a game
export function quickPickNumbers(gameId, count = 1) {
  const game = GAMES[gameId];
  if (!game) return [];

  const range = game.numberRange.max - game.numberRange.min + 1;
  const numbers = new Set();

  while (numbers.size < count) {
    const num = Math.floor(Math.random() * range) + game.numberRange.min;
    numbers.add(num);
  }

  return Array.from(numbers);
}

// Validate number for a game
export function validateNumber(gameId, number) {
  const game = GAMES[gameId];
  if (!game) return false;
  return number >= game.numberRange.min && number <= game.numberRange.max;
}

// Format number with leading zeros for display
export function formatGameNumber(gameId, number) {
  const game = GAMES[gameId];
  if (!game) return String(number);

  // Summit is explicitly a 5-digit draw per reference materials
  const maxDigits = gameId === 'summit' ? 5 : String(game.numberRange.max).length;
  return String(number).padStart(maxDigits, '0');
}

/** Comma-separated picks for one tx (e.g. `4, 06, 5` per game padding rules). */
export function formatPickedNumbersList(gameId, pickedNumberStrs) {
  if (!gameId || !Array.isArray(pickedNumberStrs) || pickedNumberStrs.length === 0) return '—';
  const parts = pickedNumberStrs
    .filter((x) => x != null && String(x) !== '')
    .map((raw) => {
      try {
        return formatGameNumber(gameId, raw);
      } catch {
        return String(raw);
      }
    });
  return parts.length ? parts.join(', ') : '—';
}

export function getMaxRange(gameId) {
  const game = GAMES[gameId];
  if (!game) return 0;
  return game.numberRange.max;
}

/**
 * Inclusive count of pickable numbers for a game (e.g. strike 0–9 → 10).
 */
export function getPickSpan(gameId) {
  const game = GAMES[gameId];
  if (!game?.numberRange) return 0;
  const { min, max } = game.numberRange;
  return max - min + 1;
}

/** Format on-chain sold count as `sold / total` using `numberRange` span. */
export function formatSoldTicketsWithTotal(sold, gameId) {
  if (sold == null) return '—';
  const soldStr =
    typeof sold === 'bigint' ? sold.toLocaleString() : Number(sold).toLocaleString();
  const total = getPickSpan(gameId);
  if (!total) return soldStr;
  return `${soldStr} / ${total.toLocaleString()}`;
}

/**
 * Deterministic draw index in [0, span) from a tx hash: strip `0x`, keep only
 * characters `0`–`9` from the hex string, take the last ceil(log10(span)) decimal
 * digits (at least one), parse as integer, then mod `span`.
 */
export function winNumberFromTxHashDigits(txHash, span) {
  const s = Number(span);
  if (!Number.isFinite(s) || s < 1) return 0;
  const hex = String(txHash ?? '').replace(/^0x/i, '');
  const digitsOnly = hex.replace(/[^0-9]/g, '');
  if (!digitsOnly) return 0;
  const width = Math.max(1, Math.ceil(Math.log10(s)));
  const tail = digitsOnly.slice(-width);
  const parsed = parseInt(tail, 10);
  const base = Number.isFinite(parsed) ? parsed : 0;
  return base % s;
}

const gameApi = {
  getGameConfig,
  getAllGameConfigs,
  placeBet,
  getCurrentDraw,
  getDrawResults,
  getRecentDraws,
  quickPickNumbers,
  validateNumber,
  formatGameNumber,
  formatPickedNumbersList,
};

export default gameApi;
